import React, {FC, useState} from "react";
import Link from "next/link";
import { useSelector } from "react-redux";
import { domainUrl } from "../../helper";
import { IState } from "../../types/state";
import { DownloadableLinkType } from "./types";
import cls from "./account.module.scss"
import Pagination from "../shared/Pagination";
import { TranslationsType } from "../../types/homePageTypes";
import { DownloadCloudIcon } from "../../svg";

const AccountPageDownloadableOrders: FC<{ dbName: string, translationsData: TranslationsType }> = ({dbName, translationsData}) => {
    const customer = useSelector((state: IState) => state.customer);
    const limit = 10;
    const [page, setPage] = useState<number>(1);
    const [okStatus, setOkStatus] = useState<boolean>(true);
    const downloadableOrders = useSelector((state: IState) => state.orders.downloadable);
    const allLinks = downloadableOrders.flatMap(order => order.items.flatMap(item =>
        item.downloadable_links.map(link => ({
            ...link,
            orderId: order.id,
            createdAt: order.created_at
        }))
    ));
    const totalPages = Math.ceil(allLinks.length / limit);
    const paginatedLinks = allLinks.slice((page - 1) * limit, page * limit);
    const [updateRequest, setUpdateRequest] = useState<boolean>(false);
    const [loaderIdCls, setLoaderIdCls] = useState<number | null>(null);
    const api_token = useSelector((state: IState) => state.cartToken.cartToken);

    const handlePageChange = (page: React.SetStateAction<number>) => {
        setPage(page);
    };

    if (!downloadableOrders) return null;

    function formatDate(date: string) {
        let cleared = date.split("-");
        let year = cleared[0];
        let month = cleared[1];
        let day = cleared[2].slice(0, 2);
        return day + "/" + month + "/" + year;
    }

    const orderStatusColor = (status: string) => {
        switch (status) {
            case ("closed"):
                return "#D71313";
            case("expired"):
                return "#4f46e5";
            case("pending"):
                return "#f4ce14";
            case("available"):
                return "#049901";
            default:
                return "#000"
        }
    }

    const ordersList = paginatedLinks.length > 0
        ? paginatedLinks.map((dwnLink: DownloadableLinkType & { orderId: number, createdAt: string }) => {
            const statusIdKey = dwnLink?.status;
            const statusKey = statusIdKey[0].toUpperCase() + statusIdKey.slice(1);
            const dwnBought = dwnLink.download_bought;
            const downloadsQty = dwnLink.download_bought - dwnLink.download_used;
            const downloadAllow = (dwnBought === 0 || downloadsQty > 0) && statusIdKey === "available" && okStatus;

            const loaderClickHandle = () => setLoaderIdCls(dwnLink.id);

            return (
                <tr className={cls.dwn_tr} key={dwnLink.id}>
                    <td className={`${cls.dwn_column} ${cls.dwn_link_column}`}>
                        <Link href={`/account/orders/${dwnLink.orderId}`}>
                            <a className={cls.order__id}>{`#${dwnLink.orderId}`}</a>
                        </Link>
                    </td>
                    <td className={`${cls.dwn_column} ${downloadAllow ? cls.dwn__active : null}`}>
                        {downloadAllow ? (
                            <div>
                                <span>{dwnLink.product_name}</span>
                                <div className={cls.download__btn_container}>
                                    <b>{translationsData.Download}</b>
                                    <button
                                        className={cls.download__btn}
                                        onClick={() => {
                                            setOkStatus(false);
                                            setUpdateRequest(!updateRequest);
                                        }}
                                    >
                                        <a target={"_blank"} href={domainUrl(`${dbName}/api/downloadable-products/download/${dwnLink.id}?token=${customer.token}&api_token=${api_token}`)}>
                                            <div onClick={loaderClickHandle} className={cls.link_container}>
                                                <div><DownloadCloudIcon/></div>
                                                <span>{dwnLink.name}</span>
                                            </div>
                                        </a>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className={cls.disabled__product_name}>
                                <span>{dwnLink.product_name}</span>
                                <div className={cls.disabled__link_container}>
                                    <b>{translationsData.Download}</b>
                                    {statusIdKey === "available" && (
                                        <div className={loaderIdCls === dwnLink.id ? "btn-loading" : ""}>
                                            <DownloadCloudIcon/>
                                        </div>
                                    )}
                                    <span>{dwnLink.name}</span>
                                </div>
                            </div>
                        )}
                    </td>
                    <td className={cls.dwn_column}>{formatDate(dwnLink.createdAt)}</td>
                    <td className={cls.dwn_column} style={{color: orderStatusColor(statusIdKey.toLowerCase())}}>
                        {translationsData[statusKey]}
                    </td>
                    <td className={cls.dwn_column}>{dwnBought === 0 ? translationsData?.UnlimitedTimes : downloadsQty}</td>
                </tr>
            );
        })
        : <span className={cls.empty_orders_list}>{translationsData.DownloadableListEmpty}</span>;

    const mobileOrderList = paginatedLinks.length > 0
        ? paginatedLinks.map((dwnLink: DownloadableLinkType & { orderId: number, createdAt: string }, index) => {
            const loaderClickHandle = () => setLoaderIdCls(dwnLink.id);
            const statusIdKey = dwnLink?.status;
            const statusKey = statusIdKey[0].toUpperCase() + statusIdKey.slice(1);
            const dwnBought = dwnLink.download_bought;
            const downloadsQty = dwnLink.download_bought - dwnLink.download_used;
            const downloadAllow = (dwnBought === 0 || downloadsQty > 0) && statusIdKey === "available" && okStatus;

            return (
                <div key={dwnLink.id} className={`${cls.card} ${cls["order-history-card-mobile"]}`}>
                    <div className={cls["order-card-mobile-top-position"]}>
                        <table className={cls.mob_dwn_table}>
                            <thead>
                            <tr className={cls.dwn_first_column}>
                                <th className={cls["order-card-mobile-title"]} style={{ textTransform: "uppercase" }}>
                                    {translationsData.OrderId}
                                </th>
                                <td>
                                    <Link href={`/account/orders/${dwnLink.orderId}`}>
                                    <span className={cls["order-id-mobile-href"]}>
                                        {`#${dwnLink.orderId}`}
                                    </span>
                                    </Link>
                                </td>
                            </tr>
                            <tr>
                                <th className={cls["order-card-mobile-title"]}>
                                    {translationsData.Product}
                                </th>
                                <td className={cls.downloads_column_mob}>
                                    {downloadAllow ? (
                                        <div className={cls.downloads__container}>
                                            <span className={cls.dw__product_name}>{dwnLink.product_name}</span>
                                            <div className={cls.download__btn_container}>
                                                <b>{translationsData.Download}</b>
                                                <button
                                                    className={cls.download__btn}
                                                    onClick={() => setUpdateRequest(!updateRequest)}
                                                >
                                                    <a
                                                        target="_blank"
                                                        href={domainUrl(`${dbName}/api/downloadable-products/download/${dwnLink.id}?token=${customer.token}&api_token=${api_token}`)}
                                                    >
                                                        <div onClick={loaderClickHandle} className={cls.link_container}>
                                                            <div><DownloadCloudIcon /></div>
                                                            <span>{dwnLink.name}</span>
                                                        </div>
                                                    </a>
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={cls.disabled__product_name}>
                                            <span>{dwnLink.product_name}</span>
                                            <div className={cls.disabled__link_container}>
                                                <b>{translationsData.Download}</b>
                                                {statusIdKey === "available" && (
                                                    <div className={loaderIdCls === dwnLink.id ? "btn-loading" : ""}>
                                                        <DownloadCloudIcon />
                                                    </div>
                                                )}
                                                <span>{dwnLink.name}</span>
                                            </div>
                                        </div>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <th>{translationsData.Date}</th>
                                <td>{formatDate(dwnLink.createdAt)}</td>
                            </tr>
                            <tr>
                                <th>{translationsData.Status}</th>
                                <td style={{ color: orderStatusColor(statusIdKey.toLowerCase()) }}>
                                    {translationsData[statusKey]}
                                </td>
                            </tr>
                            <tr>
                                <th>{translationsData.RemainingDownloads}</th>
                                <td className={cls.dwn_column}>
                                    {dwnBought === 0 ? translationsData?.UnlimitedTimes : downloadsQty}
                                </td>
                            </tr>
                            </thead>
                        </table>
                    </div>
                </div>
            );
        })
        : <span className={cls.empty_orders_list}>{translationsData.DownloadableListEmpty}</span>;

    return (
        <>
            <div className={cls.card_desktop}>
                <div className={cls.card}>
                    {
                        downloadableOrders.length > 0 ?
                            <div className={cls["card-table"]}>
                                <table>
                                    <thead>
                                    <tr>
                                        <th>
                                            {translationsData.OrderId}
                                        </th>
                                        <th>
                                            {translationsData.Name}
                                        </th>
                                        <th>
                                            {translationsData.Date}
                                        </th>
                                        <th>
                                            {translationsData.Status}
                                        </th>
                                        <th>
                                            {translationsData.RemainingDownloads}
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>{ordersList}</tbody>
                                </table>
                            </div>
                            : <span className={cls.empty_orders_list}>{translationsData.DownloadableListEmpty}</span>
                    }
                </div>
                <div className={cls["card-footer"]}>
                    {
                        totalPages > 1 && <Pagination
                            current={page}
                            total={totalPages}
                            onPageChange={handlePageChange}
                        />
                    }
                </div>
            </div>

            <div className={cls.card_mobile}>
                <div className={cls["orders-page-mobile"]}>
                    {mobileOrderList}
                </div>
                <div className={cls["card-footer"]}>
                    {
                        totalPages > 1 && <Pagination
                            current={page}
                            total={totalPages}
                            onPageChange={handlePageChange}
                        />
                    }
                </div>
            </div>
        </>
    )
};

export default AccountPageDownloadableOrders;
